import { SearchIcon } from '@chakra-ui/icons';
import {
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    Badge
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    SortingState,
    useReactTable
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import ErrorState from 'components/states/ErrorState';
import SortIndicator from 'components/table/SortIndicator';
import TablePagination, { DEFAULT_TABLE_PAGE_SIZE } from 'components/table/TablePagination';
import Menu from 'components/menu/MainMenu';
import { translate } from '../../../i18n';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import { typography } from 'theme/tokens';
import { useNavigateToUserDetails } from '../../_shared/calendar/hooks/useNavigateToUserDetails';
import { matchesNameQuery } from '../../../utils/name-search';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetUsersWithMemberships, UserWithMembershipsDto } from '../hooks/useGetUsersWithMemberships';
import Loader from '../../../components/Loader';

const columnHelper = createColumnHelper<UserWithMembershipsDto>();

export default function UsersWithMembershipsTable() {
    const { selectedOrganization } = useSelectedOrganization();
    const { data, isLoading, isFetching, error } = useGetUsersWithMemberships(selectedOrganization?.id);

    const [sorting, setSorting] = useState<SortingState>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: DEFAULT_TABLE_PAGE_SIZE,
    });
    const [searchFilter, setSearchFilter] = useState('');
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const inputBg = useColorModeValue('white', 'gray.800');
    const inputHoverBorderColor = useColorModeValue('gray.300', 'gray.600');
    const navigateToUserDetails = useNavigateToUserDetails();

    // Filter data based on search criteria
    const filteredData = useMemo(() => {
        if (!data) return [];

        return data.filter((item) => matchesNameQuery(searchFilter, item.user));
    }, [data, searchFilter]);

    const columns = useMemo(() => [
        columnHelper.accessor('user.firstName', {
            id: 'firstName',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.first_name')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.row.original.user.firstName}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('user.lastName', {
            id: 'lastName',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.last_name')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.row.original.user.lastName}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('user.email', {
            id: 'email',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('user.email')}
                </Text>
            ),
            cell: (info: any) => (
                <Flex align='center'>
                    <Text color={textColor} fontSize='sm' fontWeight='700'>
                        {info.row.original.user.email}
                    </Text>
                </Flex>
            )
        }),
        columnHelper.accessor('memberships', {
            id: 'latestMembership',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('userMembership.latest_membership')}
                </Text>
            ),
            cell: (info: any) => {
                const memberships = info.getValue();
                if (memberships.length === 0) {
                    return <Text color={textColor} fontSize='sm'>-</Text>;
                }
                const latest = memberships[0]; // Already sorted by startDate DESC
                const now = new Date();
                const hasExpired = latest.endDate ? new Date(latest.endDate) < now : false;
                const isActuallyActive = latest.isActive && !hasExpired;

                return (
                    <Flex direction='column' gap={1}>
                        <Text color={textColor} fontSize='sm' fontWeight='700'>
                            {latest.type?.name || '-'}
                        </Text>
                        <Text color='gray.500' fontSize='xs'>
                            {new Date(latest.startDate).toLocaleDateString()} - {latest.endDate ? new Date(latest.endDate).toLocaleDateString() : translate('userMembership.not_available')}
                        </Text>
                        <Flex gap={1} wrap='wrap'>
                            {isActuallyActive ? (
                                <Badge colorScheme='green' fontSize='xs' w='fit-content'>
                                    {translate('userMembership.active')}
                                </Badge>
                            ) : (
                                <Badge colorScheme='red' fontSize='xs' w='fit-content'>
                                    {translate('userMembership.expired')}
                                </Badge>
                            )}
                            {/* MYS-117 Duo (F3): beneficiary direction — info tint. */}
                            {latest.isAttachedBeneficiary && (
                                <Badge colorScheme='blue' variant='subtle' fontSize='xs' w='fit-content'>
                                    {translate('userMembership.duo_badge_beneficiary')}
                                </Badge>
                            )}
                        </Flex>
                    </Flex>
                );
            }
        }),
        columnHelper.accessor('subscriptions', {
            id: 'latestSubscription',
            header: () => (
                <Text
                    {...tableHeaderTextProps}>
                    {translate('userMembership.latest_subscription')}
                </Text>
            ),
            cell: (info: any) => {
                const subscriptions = info.getValue();
                if (subscriptions.length === 0) {
                    return <Text color={textColor} fontSize='sm'>-</Text>;
                }
                const latest = subscriptions[0]; // Already sorted by createdAt DESC
                const now = new Date();
                const periodEnded = new Date(latest.currentPeriodEnd) < now;
                const isActuallyActive = latest.isActive && !periodEnded;
                const isActuallyExpired = periodEnded || latest.isExpired;

                // Determine the label to show
                let statusLabel = latest.status;
                if (isActuallyExpired) {
                    statusLabel = translate('userMembership.expired');
                } else if (isActuallyActive) {
                    statusLabel = translate('userMembership.active');
                }

                // MYS-117 Duo (F3): direction matters — payer vs rattaché.
                const isDuoPayer = (latest.membershipType?.maxAttachedMembers ?? 0) >= 1;
                const attachedName = latest.attachedUser
                    ? `${latest.attachedUser.firstName ?? ''} ${latest.attachedUser.lastName ?? ''}`.trim()
                    : null;

                return (
                    <Flex direction='column' gap={1}>
                        <Flex align='center' gap={2} wrap='wrap'>
                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                {latest.membershipType?.name || '-'}
                            </Text>
                            {/* MYS-125 P2.2b: which sub-tariff this subscription is
                                actually charged at — undefined on legacy pre-variants
                                subscriptions (get-users-with-memberships.ts populates
                                'variant' only where the FK is set), so this badge is
                                simply absent for them rather than showing a fake default. */}
                            {latest.variant && (
                                <Badge colorScheme='purple' variant='subtle' fontSize='xs' w='fit-content'>
                                    {latest.variant.label}
                                </Badge>
                            )}
                        </Flex>
                        <Text color='gray.500' fontSize='xs'>
                            {new Date(latest.currentPeriodStart).toLocaleDateString()} - {new Date(latest.currentPeriodEnd).toLocaleDateString()}
                        </Text>
                        <Flex gap={1} wrap='wrap'>
                            <Badge
                                colorScheme={isActuallyActive ? 'green' : isActuallyExpired ? 'red' : 'gray'}
                                fontSize='xs'
                                w='fit-content'>
                                {statusLabel}
                            </Badge>
                            {isDuoPayer && (
                                <Badge colorScheme='brand' variant='subtle' fontSize='xs' w='fit-content'>
                                    {translate('userMembership.duo_badge_payer')}
                                </Badge>
                            )}
                        </Flex>
                        {attachedName && (
                            <Text color='gray.500' fontSize='xs'>
                                {translate('userMembership.duo_attached')} {attachedName}
                            </Text>
                        )}
                    </Flex>
                );
            }
        })
    ], [textColor]);

    const table = useReactTable({
        data: filteredData,
        columns,
        state: {
            sorting,
            pagination,
        },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        // Same freeze as `UserList`: `GET users/with-memberships` is the whole
        // membership roster in one response and every row was committed to the
        // DOM at once. One page per render; `autoResetPageIndex` returns to
        // page 1 when the search or the organization changes.
        getPaginationRowModel: getPaginationRowModel(),
    });

    return (
        <Card flexDirection='column' w='100%' px='0px' overflowX={{ sm: 'scroll', lg: 'hidden' }}>
            <Flex px='25px' mb="8px" justifyContent='space-between' align='center'>
                <Text color={textColor} {...typography.pageTitle}>
                    {translate('userMembership.users_with_memberships_title')}
                </Text>
                <Menu />
            </Flex>

            {/* Search Filter */}
            <Flex px='25px' mb="16px">
                <InputGroup maxW={{ base: '100%', md: '400px' }}>
                    <InputLeftElement pointerEvents='none'>
                        <SearchIcon color='gray.400' />
                    </InputLeftElement>
                    <Input
                        placeholder={translate('userMembership.search_placeholder')}
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        bg={inputBg}
                        borderColor={borderColor}
                        _hover={{
                            borderColor: inputHoverBorderColor
                        }}
                        _focus={{
                            borderColor: 'brand.500',
                            boxShadow: '0 0 0 1px brand.500'
                        }}
                    />
                </InputGroup>
            </Flex>

            <Box>
                <Table {...tableProps}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            {...tableThProps}
                                            borderColor={borderColor}
                                            cursor='pointer'
                                            onClick={header.column.getToggleSortingHandler()}>
                                            <Flex
                                                {...tableThFlexProps}>
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                <SortIndicator direction={header.column.getIsSorted()} />
                                            </Flex>
                                        </Th>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <Tr
                                    key={row.id}
                                    {...tableRowProps}
                                    onClick={() => {
                                        navigateToUserDetails?.(row.original.user.id);
                                    }}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                key={cell.id}
                                                {...tableCellProps}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
                <TablePagination
                    pageIndex={table.getState().pagination.pageIndex}
                    pageCount={table.getPageCount()}
                    totalCount={filteredData.length}
                    canPrevious={table.getCanPreviousPage()}
                    canNext={table.getCanNextPage()}
                    onPrevious={() => table.previousPage()}
                    onNext={() => table.nextPage()}
                />
                <Loader isLoading={(isLoading || isFetching) && ((data || []).length === 0)} />
                {error && (
                    <ErrorState message={translate('userMembership.error_loading')} />
                )}
                {!isLoading && !isFetching && filteredData.length === 0 && (
                    <Box textAlign="center" py={8}>
                        <Text color="gray.500">{translate('userMembership.no_users_found')}</Text>
                    </Box>
                )}
            </Box>
        </Card>
    );
}
